import { IFilterService } from "angular";

import {
  alphanumericComparator,
  sortingRulesToOrderStrings,
  toItemsTreeOrderField,
  expandedSelfIdsKey,
  getPersistedExpandedGoals,
  expandedGoalsKey,
  resetPersistedExpandedItems,
  getPersistedExpandedSelfIds,
} from "./utils/index";
import { GoalParentType } from "@gtmhub/goals/models";
import { IMetricLinksSpec } from "../metrics";
import {
  IOkrsAlignmentFiltersApplied,
  IItemsTreeAggregatedFilters,
  IItemsAlignmentData,
  IItemsTreeAdditionalData,
  IAlignmentTreeMetric,
  IAlignmentTreeGoal,
} from "./models";
import { IdMap } from "@gtmhub/util";
import { IItem, ICrossContributorLink } from "./renderer/models";
import { ProgressColoring } from "@gtmhub/configuration";
import { byTextField, byNumericFieldDesc, byEach, byNumericField, byBooleanField } from "@gtmhub/util/sorting";
import { splitArrayIntoTwo } from "@gtmhub/shared/components";
import { IAssignee, IDeletedAssignee, deletedAssignee, isDeletedAssignee } from "@gtmhub/assignees";
import { MetricValueFilter } from "@gtmhub/okrs/metrics";
import { IGridSortingRule, setPersistedScreenSettings } from "@gtmhub/shared/components/grid-filtering-bar";
import { expandedGoalsScreenPath } from "@gtmhub/goals/filter-utils";

const topLevelParentId = "";

export class ItemsTreeService {
  public static $inject = ["$filter", "ProgressColoring"];

  constructor(private $filter: IFilterService, private progressColoring: ProgressColoring) {}

  public applyWSChanges(itemsData: IItemsAlignmentData, updatedGoals: IAlignmentTreeGoal[], updatedMetrics: IAlignmentTreeMetric[]): void {
    if (!itemsData || !itemsData.idMap) {
      return;
    }

    (updatedGoals || []).forEach(({ id, attainment, fullAggregatedAttainment }) => {
      const item = itemsData.idMap[id];
      if (item) {
        item.attainment = this.progressColoring.wrapProgress(attainment);
        item.fullAggregatedAttainment = this.progressColoring.wrapProgress(fullAggregatedAttainment);
      }
    });

    (updatedMetrics || []).forEach((metric) => {
      const { id, attainment } = metric;
      const item = itemsData.idMap[id];
      if (item) {
        item.attainment = this.progressColoring.wrapProgress(attainment);
        item.crossContributorLinks.forEach((link) => {
          link.formattedContributionValue = this.$filter<MetricValueFilter>("metricValue")(metric.actual, metric.format);
          link.contributionValue = link.calulateContributionValue(metric);
        });
      }
    });
  }

  public updateMetricNode(itemsData: IItemsAlignmentData, metric: IAlignmentTreeMetric, metricParentGoal: IAlignmentTreeGoal, data: IItemsTreeAdditionalData): void {
    const parentGoalItemNode = itemsData.idMap[metricParentGoal.id];
    const updatedItemNode = this.generateItemNodeFromMetric(metric, parentGoalItemNode, data);
    itemsData.idMap[metric.id] = angular.extend(itemsData.idMap[metric.id], updatedItemNode);
  }

  public showHiddenChildItems(parent: IItem | null, itemsData: IItemsAlignmentData): void {
    const childNodesToShow = parent ? [...parent.supporters, ...parent.contributors] : itemsData.tree;

    childNodesToShow.forEach((node) => {
      if (node.hidden) {
        node.hidden = false;

        const nextParent = node;
        this.showHiddenChildItems(nextParent, itemsData);
      }
    });
  }

  public toggleItemSupporters(itemId: string, itemsData: IItemsAlignmentData, sessionId: string): void {
    const expandedGoals: IdMap<boolean> = getPersistedExpandedGoals(sessionId);

    const newExpandedStatus = !itemsData.idMap[itemId].supportersExpanded;

    itemsData.idMap[itemId].supportersExpanded = newExpandedStatus;
    expandedGoals[itemId] = newExpandedStatus;

    if (newExpandedStatus) {
      // if expanding => revealing direct children so they won't appear hidden after expanding
      this.makeDirectChildNodesVisible(itemId, itemsData);
    } else {
      // if item is being collapsed - delete its entry before persisting
      delete expandedGoals[itemId];
    }

    setPersistedScreenSettings(expandedGoalsScreenPath(sessionId), expandedGoalsKey, expandedGoals);
  }

  public toggleItemSelf(itemId: string, itemsData: IItemsAlignmentData, sessionId: string): void {
    const expandedSelfIds = getPersistedExpandedSelfIds(sessionId);
    const newSelfExpandedStatus = !itemsData.idMap[itemId].contributorSelfExpanded;

    itemsData.idMap[itemId].contributorSelfExpanded = newSelfExpandedStatus;
    expandedSelfIds[itemId] = newSelfExpandedStatus;

    // if collapsing => toggle self + supporters
    if (!newSelfExpandedStatus) {
      const expandedGoals = getPersistedExpandedGoals(sessionId);

      itemsData.idMap[itemId].supportersExpanded = newSelfExpandedStatus;
      // if item is being collapsed - delete its entries before persisting
      delete expandedGoals[itemId];
      delete expandedSelfIds[itemId];

      setPersistedScreenSettings(expandedGoalsScreenPath(sessionId), expandedGoalsKey, expandedGoals);
    }

    setPersistedScreenSettings(expandedGoalsScreenPath(sessionId), expandedSelfIdsKey, expandedSelfIds);
  }

  public expandAllItems(itemsData: IItemsAlignmentData, currentSessionId: string): void {
    const expandedItemsBooleanMap: IdMap<boolean> = {};
    const expandedSelfBooleanMap: IdMap<boolean> = {};

    for (const item of Object.values(itemsData.idMap)) {
      item.supportersExpanded = true;
      item.contributorSelfExpanded = true;
      item.hidden = false;

      expandedItemsBooleanMap[item.id] = true;
      expandedSelfBooleanMap[item.id] = true;
    }

    const screenPath = expandedGoalsScreenPath(currentSessionId);
    setPersistedScreenSettings(screenPath, expandedGoalsKey, expandedItemsBooleanMap);
    setPersistedScreenSettings(screenPath, expandedSelfIdsKey, expandedSelfBooleanMap);
  }

  public collapseAllItems(itemsData: IItemsAlignmentData, currentSessionId: string): void {
    for (const item of Object.values(itemsData.idMap)) {
      item.supportersExpanded = false;
      item.contributorSelfExpanded = false;
    }

    resetPersistedExpandedItems(currentSessionId);
  }

  private generateAggregatedFilters(filterOptions: IOkrsAlignmentFiltersApplied, currentSessionId: string): IItemsTreeAggregatedFilters {
    const expandedItemsIdsMap = getPersistedExpandedGoals(currentSessionId);
    const expandedSelfIdsMap = getPersistedExpandedSelfIds(currentSessionId);

    const isSharedIdPresent = !!filterOptions.sharedId;
    const isSharedParentIdPresent = !!filterOptions.sharedParentId;
    const isLocateIdPresent = !!filterOptions.locateGoalId;

    return {
      filterResult: filterOptions.filterResult,
      sharedId: isSharedIdPresent
        ? {
            id: filterOptions.sharedId,
          }
        : null,
      sharedParentId: isSharedParentIdPresent
        ? {
            id: filterOptions.sharedParentId,
          }
        : null,
      locateItem: isLocateIdPresent ? { id: filterOptions.locateGoalId } : null,
      persisted: expandedItemsIdsMap
        ? {
            idsBooleanMap: expandedItemsIdsMap,
          }
        : null,
      persistedSelf: expandedSelfIdsMap
        ? {
            idsBooleanMap: expandedSelfIdsMap,
          }
        : null,
    };
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public constructItemsTreeFromOkrs(
    goals: IAlignmentTreeGoal[],
    filterOptions: IOkrsAlignmentFiltersApplied,
    data: IItemsTreeAdditionalData,
    additionalInfo: {
      isNestedViewV2Enabled: boolean;
      isReorderGoalChildrenFeatureEnabled: boolean;
    }
  ): IItemsAlignmentData {
    const itemExistenceMap: IdMap<boolean> = {};
    const parentIdItemsMap: IdMap<IAlignmentTreeGoal[]> = {
      [topLevelParentId]: [],
    };

    goals.sort(byTextField("name")).forEach((goal) => {
      if (!parentIdItemsMap[goal.parentId]) {
        parentIdItemsMap[goal.parentId] = [];
      }
      parentIdItemsMap[goal.parentId].push(goal);
      itemExistenceMap[goal.id] = true;

      // orderBy : ['name', '-attainment']"
      (goal.metrics || []).sort(byEach(byTextField("name"), byNumericFieldDesc("attainment")));
      (goal.metrics || []).forEach((metric) => (itemExistenceMap[metric.id] = true));
    });

    // dealing with goals whose parent is not present in current collection
    // moving them as top level goals
    const parentIds = Object.keys(parentIdItemsMap);
    parentIds.forEach((parentId) => {
      if (parentId !== topLevelParentId && !itemExistenceMap[parentId]) {
        parentIdItemsMap[topLevelParentId].push(...parentIdItemsMap[parentId]);
      }
    });

    const itemsData: IItemsAlignmentData = {
      tree: [],
      idMap: {},
      meta: {
        expandedIds: [],
        expandedSelfIds: [],
      },
    };

    const aggregatedFilters = this.generateAggregatedFilters(filterOptions, data.currentSessionId);
    this.fillItemsTreeWithOkrs(null, itemsData, parentIdItemsMap, data, aggregatedFilters);

    if (data.order && data.order.length) {
      itemsData.tree = this.sortItemsTree(itemsData.tree, data, additionalInfo.isReorderGoalChildrenFeatureEnabled, additionalInfo.isNestedViewV2Enabled);
    }

    const filtersAppliedAndNoPersistedExpandedItems = this.filtersAppliedAndNoPersistedExpandedItems(aggregatedFilters, data);

    // check if any goals with supportersExpanded are left with no supporters
    itemsData.meta.expandedIds.forEach((expandedItemId) => this.collapseSupportersIfNoSupportersLeft(expandedItemId, itemsData, data.currentSessionId));

    // expand and display the items on the path from matches to root
    const matchedNodeIds = filterOptions.filterResult ? filterOptions.filterResult.goalIds : [];
    this.updateVisibilityDataOfParentNodes(matchedNodeIds, itemsData, aggregatedFilters, { shouldForceExpandMatchesParents: filtersAppliedAndNoPersistedExpandedItems });

    // remove hidden status from direct children of expanded items
    itemsData.meta.expandedIds.forEach((expandedItemId) => this.makeDirectChildNodesVisible(expandedItemId, itemsData));

    if (filtersAppliedAndNoPersistedExpandedItems) {
      this.persistExpandedItems(itemsData, data.currentSessionId);
    }

    const shouldCenterViewToItem = (aggregatedFilters.locateItem && aggregatedFilters.locateItem.id) || (aggregatedFilters.sharedId && aggregatedFilters.sharedId.id);
    // expand the path to the goal with shared id if such
    if (shouldCenterViewToItem) {
      const itemToCenterId = this.getItemToCenterId(aggregatedFilters, itemsData);
      this.updateVisibilityDataOfParentNodes([itemToCenterId], itemsData, aggregatedFilters, {
        shouldForceExpandMatchesParents: true,
      });
    }

    return itemsData;
  }

  private getItemToCenterId(aggregatedFilters: IItemsTreeAggregatedFilters, itemsData: IItemsAlignmentData): string {
    const itemId = aggregatedFilters.locateItem ? aggregatedFilters.locateItem.id : aggregatedFilters.sharedId.id;
    if (itemsData.idMap[itemId].type === "goal") {
      return itemId;
    }
    // if sharedId is of a metric, returns the id of its parent goal so it can be expanded
    return Object.values(itemsData.idMap).find((item) => item.contributors.some((c) => c.id === itemId)).id;
  }

  private filtersAppliedAndNoPersistedExpandedItems(aggregatedFilters: IItemsTreeAggregatedFilters, data: IItemsTreeAdditionalData): boolean {
    const anyFiltersApplied = Boolean(aggregatedFilters.filterResult && aggregatedFilters.filterResult.goalIds && aggregatedFilters.filterResult.goalIds.length);

    return (
      anyFiltersApplied &&
      !Object.keys(getPersistedExpandedGoals(data.currentSessionId)).length &&
      !Object.keys(getPersistedExpandedSelfIds(data.currentSessionId)).length
    );
  }

  private persistExpandedItems(itemsData: IItemsAlignmentData, currentSessionId: string): void {
    const expandedItemsBooleanMap: IdMap<boolean> = {};
    const expandedSelfBooleanMap: IdMap<boolean> = {};

    for (const item of Object.values(itemsData.idMap)) {
      if (item.supportersExpanded) {
        expandedItemsBooleanMap[item.id] = true;
      }

      if (item.contributorSelfExpanded) {
        expandedSelfBooleanMap[item.id] = true;
      }
    }

    const screenPath = expandedGoalsScreenPath(currentSessionId);
    setPersistedScreenSettings(screenPath, expandedGoalsKey, expandedItemsBooleanMap);
    setPersistedScreenSettings(screenPath, expandedSelfIdsKey, expandedSelfBooleanMap);
  }

  public sortItemsTree(itemNodes: IItem[], data: IItemsTreeAdditionalData, isReorderGoalChildrenFeatureEnabled = false, isNestedViewV2Enabled: boolean): IItem[] {
    const itemsTreeOrder: IGridSortingRule[] = data.order.map((sortingRule) => ({ ...sortingRule, fieldName: toItemsTreeOrderField(sortingRule.fieldName) }));

    const sortObfuscatedGoals = (nodes: IItem[]): IItem[] => {
      return nodes.sort(byBooleanField("obfuscated"));
    };

    const sortOwners = (nodes: IItem[], rule: IGridSortingRule): void => {
      const order = (x: IAssignee | IDeletedAssignee, y: IAssignee | IDeletedAssignee) => {
        let a = x;
        let b = y;
        if (rule.reversed) {
          a = y;
          b = x;
        }
        if (isDeletedAssignee(a)) {
          return 1;
        }
        if (isDeletedAssignee(b)) {
          return -1;
        }
        return a.name.localeCompare(b.name);
      };
      for (const n of nodes) {
        const sorted = [...n.owners.additional, ...n.owners.preview].sort(order);
        n.sortedOwner = sorted[0];
      }
    };

    const sortNodeContributorsByOrderIdRecursively = (node: IItem): void => {
      node.contributors.sort(byNumericField("orderId"));
      node.contributors.forEach(sortNodeContributorsByOrderIdRecursively);
      if (isNestedViewV2Enabled) {
        node.supporters.sort(byBooleanField("obfuscated"));
      }
      node.supporters.forEach(sortNodeContributorsByOrderIdRecursively);
    };

    const sort = (nodes: IItem[]) => {
      const assigneesSort = itemsTreeOrder.find((rule) => rule.fieldName === "sortedOwner.name");
      // since we have multiple owners, we need to first sort the array and get the first/last
      if (assigneesSort) {
        sortOwners(nodes, assigneesSort);
      }

      let sortedNodes = this.$filter("orderBy")(nodes, sortingRulesToOrderStrings(itemsTreeOrder), false, alphanumericComparator);

      sortedNodes.forEach((n) => {
        n.supporters = sort(n.supporters);
        if (isReorderGoalChildrenFeatureEnabled) {
          sortNodeContributorsByOrderIdRecursively(n);
        }
      });

      if (isNestedViewV2Enabled) {
        sortedNodes = sortObfuscatedGoals(sortedNodes);
      }

      return sortedNodes;
    };

    return sort(itemNodes);
  }

  private updateVisibilityDataOfParentNodes(
    nodeIds: string[],
    itemsData: IItemsAlignmentData,
    aggregatedFilters: IItemsTreeAggregatedFilters,
    additionalInfo: {
      shouldForceExpandMatchesParents: boolean;
    }
  ): void {
    nodeIds.forEach((nodeId) => {
      const item = itemsData.idMap[nodeId];
      if (item) {
        let nextParentItemId = item.parent ? item.parent.id : null;

        while (nextParentItemId) {
          const parentItem = itemsData.idMap[nextParentItemId];
          if (parentItem) {
            parentItem.supportersExpanded = aggregatedFilters.persisted.idsBooleanMap[parentItem.id] || additionalInfo.shouldForceExpandMatchesParents;
            parentItem.contributorSelfExpanded = true;
            item.contributorSelfExpanded = true;
            parentItem.hidden = false;
          }

          nextParentItemId = parentItem && parentItem.parent ? parentItem.parent.id : null;
        }

        if (this.checkIfContributorSelfShouldBeExpanded(item)) {
          this.makeContributorSelfNodeVisible(item);
          itemsData.meta.expandedSelfIds.push(item.id);
        }
      }
    });
  }

  private makeDirectChildNodesVisible(parentItemId: string, itemsData: IItemsAlignmentData): void {
    [...itemsData.idMap[parentItemId].supporters, ...itemsData.idMap[parentItemId].contributors].forEach((node) => (node.hidden = false));
  }

  private collapseSupportersIfNoSupportersLeft(parentItemId: string, itemsData: IItemsAlignmentData, sessionId: string): void {
    if (!itemsData.idMap[parentItemId].supporters.length) {
      this.toggleItemSupporters(parentItemId, itemsData, sessionId);
    }
  }

  private makeContributorSelfNodeVisible(itemNode: IItem): void {
    itemNode.contributorSelfExpanded = true;
  }

  private checkIfContributorSelfShouldBeExpanded(itemNode: IItem): boolean {
    // contributor with contributors
    const hasSelfNode = itemNode.contributors.length && itemNode.parent && itemNode.parent.relation === "contribution";
    // self node is expanded / last node => has no supporters and its contributors have no supporters/contributors
    const selfNodeShouldBeExpanded =
      itemNode.contributorSelfExpanded ||
      (!itemNode.supporters.length && !itemNode.contributors.filter((sup) => sup.supporters.length || sup.contributors.length).length);

    return hasSelfNode && selfNodeShouldBeExpanded;
  }

  private fillItemsTreeWithOkrs(
    parent: { id: string; type: GoalParentType; attainmentTypeString?: string } | null,
    itemsData: IItemsAlignmentData,
    parentIdItemsMap: IdMap<IAlignmentTreeGoal[]>,
    data: IItemsTreeAdditionalData,
    aggregatedFilters: IItemsTreeAggregatedFilters
  ): void {
    const parentId = parent ? parent.id : topLevelParentId;
    parentIdItemsMap[parentId].forEach((goal) => {
      const goalItemNode = this.generateItemNodeFromGoal(goal, data, aggregatedFilters);
      itemsData.idMap[goal.id] = goalItemNode;
      this.updateItemsDataMeta(itemsData, goalItemNode);

      const metrics = goal.metrics || [];
      if (goal.attainmentTypeString === "average_kr" || goal.attainmentTypeString === "average_children_kr") {
        metrics.forEach((metric) => {
          const metricItemNode = this.generateItemNodeFromMetric(metric, goalItemNode, data, aggregatedFilters);
          goalItemNode.contributors.push(metricItemNode);
          itemsData.idMap[metric.id] = metricItemNode;
        });
      }

      const parentNode = itemsData.idMap[parentId];
      if (!parentNode) {
        itemsData.tree.push(goalItemNode);
      } else {
        if (parent.attainmentTypeString === "average_children" || parent.attainmentTypeString === "average_children_kr") {
          goalItemNode.parent.relation = "contribution";
          parentNode.contributors.push(goalItemNode);
        } else {
          goalItemNode.parent.relation = "support";
          parentNode.supporters.push(goalItemNode);
        }
      }

      if (parentIdItemsMap[goal.id]) {
        this.fillItemsTreeWithOkrs({ id: goal.id, type: "goal", attainmentTypeString: goal.attainmentTypeString }, itemsData, parentIdItemsMap, data, aggregatedFilters);
      }

      if (goal.attainmentTypeString === "average_kr" || goal.attainmentTypeString === "average_children_kr") {
        metrics.forEach((metric) => {
          if (parentIdItemsMap[metric.id]) {
            this.fillItemsTreeWithOkrs({ id: metric.id, type: "metric" }, itemsData, parentIdItemsMap, data, aggregatedFilters);
          }
        });
      }
    });
  }
  private generateOwnersVisualizationProps(owners: (IAssignee | IDeletedAssignee)[]): {
    previewOwners: IAssignee[];
    additionalOwners: IAssignee[];
    ownersIconsCount: number;
  } {
    let previewOwnerIds = [];
    let additionalOwnerIds = [];
    let ownersIconsCount = 0;
    if (owners) {
      const maxOwnersIconsCount = 3;
      const { firstPart, secondPart } = splitArrayIntoTwo(owners, maxOwnersIconsCount);
      previewOwnerIds = firstPart;
      additionalOwnerIds = secondPart;
      ownersIconsCount = previewOwnerIds.length;
      if (additionalOwnerIds.length) {
        ownersIconsCount++;
      }
    }
    return { previewOwners: previewOwnerIds, additionalOwners: additionalOwnerIds, ownersIconsCount };
  }

  private generateItemNodeFromGoal(goal: IAlignmentTreeGoal, data: IItemsTreeAdditionalData, aggregatedFilters: IItemsTreeAggregatedFilters): IItem {
    const anyFiltersApplied = !!aggregatedFilters.filterResult;
    const matchesFilter = anyFiltersApplied && aggregatedFilters.filterResult.goalIds.includes(goal.id);
    const isShared = aggregatedFilters.sharedId && aggregatedFilters.sharedId.id && aggregatedFilters.sharedId.id === goal.id;
    const located = aggregatedFilters.locateItem && aggregatedFilters.locateItem.id === goal.id;
    const highlighted = aggregatedFilters.sharedParentId && aggregatedFilters.sharedParentId.id === goal.id;

    // hidden and expanded will be updated after tree is fully constructed for some of the nodes on the path to expanded/matched nodes
    const supportersExpanded = aggregatedFilters.persisted && aggregatedFilters.persisted.idsBooleanMap ? !!aggregatedFilters.persisted.idsBooleanMap[goal.id] : false;
    const contributorSelfExpanded =
      aggregatedFilters.persistedSelf && aggregatedFilters.persistedSelf.idsBooleanMap ? !!aggregatedFilters.persistedSelf.idsBooleanMap[goal.id] : false;
    const hidden = supportersExpanded || located || isShared ? false : anyFiltersApplied ? !matchesFilter : false;

    const owners = goal.ownerIds ? goal.ownerIds.map((id) => data.assigneesIdMap[id] || deletedAssignee(id)) : [];
    const { previewOwners, additionalOwners, ownersIconsCount } = this.generateOwnersVisualizationProps(owners);

    return {
      id: goal.id,
      type: "goal",
      title: goal.name,
      designScore: goal.designScore,
      attainment: this.progressColoring.wrapProgress(goal.attainment),
      fullAggregatedAttainment: this.progressColoring.wrapProgress(goal.fullAggregatedAttainment),
      owners: {
        preview: goal.ownerIds
          ? previewOwners.map((assignee, i) => {
              return { ...assignee, index: i, iconsCount: ownersIconsCount };
            })
          : [],
        additional: goal.ownerIds ? additionalOwners : [],
      },
      session: {
        id: goal.sessionId,
        color: data.sessionIdMap[goal.sessionId].color,
      },
      private: goal.private,
      obfuscated: goal.obfuscated,
      locked: goal.locked,
      workflow: goal.workflow && goal.workflow.status ? { status: goal.workflow.status } : null,
      contributors: [],
      supporters: [],
      crossContributorLinks: [],
      matchesFilter,
      located,
      highlighted,
      // hidden and expanded will be updated after tree is fully constructed for some of the nodes on the path to expanded/matched nodes
      supportersExpanded,
      contributorSelfExpanded: contributorSelfExpanded,
      hidden,
      parent: goal.parentId
        ? {
            type: goal.parentType,
            id: goal.parentId,
            relation: "support",
          }
        : null,
      ...(goal.orderId !== undefined && { orderId: goal.orderId }),
    };
  }

  private updateItemsDataMeta(itemsData: IItemsAlignmentData, itemNode: IItem): void {
    // this logic is currently applied to supporter(goal) nodes only although their contributors inherit expanded
    if (itemNode.supportersExpanded) {
      // add to expandedIds needed to update expanded/hidden props in nodes based on persisted settings after tree is fully constructed
      itemsData.meta.expandedIds.push(itemNode.id);
    }
    if (itemNode.contributorSelfExpanded) {
      itemsData.meta.expandedSelfIds.push(itemNode.id);
    }
  }

  private generateItemNodeFromMetric(
    metric: IAlignmentTreeMetric,
    parentItemNode: IItem,
    data: IItemsTreeAdditionalData,
    aggregatedFilters?: IItemsTreeAggregatedFilters
  ): IItem {
    const supportersExpanded =
      aggregatedFilters && aggregatedFilters.persisted && aggregatedFilters.persisted.idsBooleanMap ? !!aggregatedFilters.persisted.idsBooleanMap[metric.id] : false;

    const owners = metric.ownerIds ? metric.ownerIds.map((id) => data.assigneesIdMap[id] || deletedAssignee(id)) : [];
    const { previewOwners, additionalOwners, ownersIconsCount } = this.generateOwnersVisualizationProps(owners);

    return {
      id: metric.id,
      type: "metric",
      title: metric.name,
      attainment: this.progressColoring.wrapProgress(metric.attainment),
      owners: {
        preview: metric.ownerIds
          ? previewOwners.map((assignee, i) => {
              return { ...assignee, index: i, iconsCount: ownersIconsCount };
            })
          : [],
        additional: metric.ownerIds ? additionalOwners : [],
      },
      ...(metric.sourceMetricId && { sourceMetricId: metric.sourceMetricId }),
      session: { ...parentItemNode.session },
      private: parentItemNode.private,
      obfuscated: parentItemNode.obfuscated,
      locked: parentItemNode.locked,
      workflow: parentItemNode.workflow,
      contributors: [],
      supporters: [],
      inboundCrossContributorLinksAllowed: metric.cascadeType === "absolute_value" || metric.cascadeType === "delta_value",
      crossContributorLinks: this.generateCrossContributorLinks(metric, data),
      hidden: parentItemNode.hidden,
      supportersExpanded,
      contributorSelfExpanded: false,
      matchesFilter: parentItemNode.matchesFilter,
      parent: metric.goalId
        ? {
            type: "goal",
            id: metric.goalId,
            relation: "contribution",
          }
        : null,
      ...(metric.orderId !== undefined && { orderId: metric.orderId }),
    };
  }

  private generateCrossContributorLinks(metric: IAlignmentTreeMetric, data: IItemsTreeAdditionalData): ICrossContributorLink[] {
    const links: ICrossContributorLink[] = [];
    const spec: IMetricLinksSpec = (metric.links ? metric.links.spec : {}) || {};

    const deltaCalculationStrategy = (metric: IAlignmentTreeMetric): number => metric.actual - metric.initialValue;
    const actualValueCalculationStrategy = (metric: IAlignmentTreeMetric): number => metric.actual;

    if (spec.contributing) {
      links.push(
        ...spec.contributing.map<ICrossContributorLink>((link) => {
          const calculateContributionStrategy = data.metricsIdMap[link.id]?.cascadeType === "absolute_value" ? actualValueCalculationStrategy : deltaCalculationStrategy;
          return {
            id: link.id,
            type: link.type,
            direction: "outbound",
            formattedContributionValue: this.$filter<MetricValueFilter>("metricValue")(metric.actual, metric.format),
            contributionValue: calculateContributionStrategy(metric),
            calulateContributionValue: calculateContributionStrategy,
          };
        })
      );
    }

    if (spec.cascading) {
      links.push(
        ...spec.cascading.map<ICrossContributorLink>((link) => ({
          id: link.id,
          type: link.type,
          direction: "inbound",
          formattedContributionValue: this.$filter<MetricValueFilter>("metricValue")(metric.actual, metric.format),
          contributionValue: actualValueCalculationStrategy(metric),
          calulateContributionValue: actualValueCalculationStrategy,
        }))
      );
    }

    return links;
  }
}
